import { createFileRoute } from "@tanstack/react-router";
import { Lock, ShieldCheck, KeyRound } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import pin1Asset from "@/assets/platform/networking/pin-protection/pin-1.webp.asset.json";
import pin2Asset from "@/assets/platform/networking/pin-protection/pin-2.webp.asset.json";
import pin3Asset from "@/assets/platform/networking/pin-protection/pin-3.webp.asset.json";

const heroImg = pin1Asset.url;
const heroAlt = "Thẻ danh thiếp số Banana khóa PIN trên iPhone hiển thị 'This card is locked' với ô nhập PIN 4 chữ số phủ trên hồ sơ tối màu John Murray";
const controlImg = pin2Asset.url;
const controlAlt = "Luồng cài PIN thẻ Banana — widget Protect Your Card và modal 'Enter a 4-Digit Code' cạnh hồ sơ đã xuất bản Arden Le Minh trên iPhone";
const useCaseImg = pin3Asset.url;
const useCaseAlt = "Nhiều iPhone hiển thị các trạng thái bảo vệ PIN Banana — Setup a PIN Code, màn Enter PIN, thẻ bị khóa và hồ sơ Hiroshi Kojima đã mở khóa";

export const Route = createFileRoute("/vi/platform/networking/pin-protection")({
  head: () => ({
    meta: [
      { title: "Thẻ bảo vệ PIN — banana.cards" },
      { name: "description", content: "Khóa thẻ bằng PIN 4 chữ số. Chỉ người bạn cho mã mới mở khóa và xem hồ sơ." },
      { property: "og:title", content: "Thẻ bảo vệ PIN — banana.cards" },
      { property: "og:description", content: "Khóa thẻ bằng PIN 4 chữ số." },
      { property: "og:image", content: heroImg },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/networking/pin-protection", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Bảo vệ thẻ"
      title="Thông tin của bạn,"
      italic="sau một PIN."
      description="Thêm PIN 4 chữ số vào thẻ số. Ai scan hay tap thấy màn khóa — chỉ người có mã mới mở khóa hồ sơ đầy đủ."
      heroImage={heroImg}
      heroImageAlt={heroAlt}
      metrics={[{ v: "4 chữ số", l: "PIN" }, { v: "Mỗi thẻ", l: "kiểm soát" }, { v: "Toggle", l: "bất cứ lúc nào" }]}
      bullets={[
        { icon: Lock, title: "Khóa bằng mã", desc: "Chọn PIN 4 chữ số mỗi thẻ — đổi hoặc bỏ bất cứ lúc nào." },
        { icon: ShieldCheck, title: "Riêng tư theo yêu cầu", desc: "Cho thẻ VIP, chia sẻ ngoài giờ, hoặc dữ liệu nhạy cảm." },
        { icon: KeyRound, title: "Dễ chia sẻ", desc: "Chỉ cho PIN cho người bạn tin — không account, không login bên họ." }
      ]}
      deepDives={[
        { eyebrow: "Kiểm soát", title: "Mặt công khai,", italic: "thông tin riêng tư.", description: "Hiện teaser — tên, chức danh, ảnh — giữ phone, email và link sau khóa. Hoàn hảo cho executive và team nhạy cảm.", image: controlImg, imageAlt: controlAlt, reverse: false, points: ["Teaser công khai vs hồ sơ riêng", "Tùy chỉnh gì gated", "Đổi PIN bất cứ lúc nào", "Audit ai mở khóa khi nào"] },
        { eyebrow: "Use case", title: "Thiết kế cho", italic: "chia sẻ tin cậy cao.", description: "Founder, luật sư, executive, team security — ai muốn chia sẻ chọn lọc không in nhiều thẻ.", image: useCaseImg, imageAlt: useCaseAlt, reverse: true, points: ["Thẻ executive", "Truy cập khách VIP", "Chia sẻ nội bộ vs ngoài", "Truy cập chỉ trong tối sự kiện"] }
      ]}
      howItWorks={[
        { title: "Đặt PIN 4 chữ số", desc: "Từ settings thẻ — 10 giây." },
        { title: "Chia sẻ thẻ", desc: "Như thường — người nhận gặp màn khóa." },
        { title: "Cho họ mã", desc: "Bằng giọng nói, message hoặc giấy." },
        { title: "Họ mở khóa", desc: "Hồ sơ đầy đủ hiện sau khi verify." }
      ]}
      capabilities={["PIN 4 chữ số mỗi thẻ", "Đổi PIN bất cứ lúc nào", "Màn teaser công khai", "Trường gated tùy chỉnh", "On/off mỗi thẻ", "Log mở khóa", "Bảo vệ brute-force", "Hoạt động mọi kênh chia sẻ"]}
      faq={[
        { q: "Vì sao PIN-protect thẻ Banana?", a: "Hữu ích khi thẻ ở tay người khác hoặc media vật lý chung: thẻ NFC ở quầy lễ tân chỉ mở cho khách đã sàng lọc, thẻ khách giá trị cao, thẻ team chứa danh bạ private." },
        { q: "Mỗi thẻ có PIN khác nhau được không?", a: "Có — mỗi thẻ có PIN độc lập. 1 người có thể có 1 thẻ PIN (executive) và 1 thẻ không PIN (công khai) cùng lúc." },
        { q: "Có thấy ai mở khóa thẻ khi nào không?", a: "Có — mỗi lần mở khóa thành công được log với timestamp, vị trí và device. Thất bại log riêng để phát hiện probing." },
        { q: "Có chống brute-force không?", a: "Có — sau vài lần sai, thẻ bị rate-limit, log lại, và Pro/Enterprise có alert khi thẻ bị tấn công." }
      ]}
      outro={{ title: "Chia sẻ gì bạn muốn.", italic: "Giấu gì không.", description: "Bảo vệ PIN cho bạn kiểm soát ai thấy gì." }}
    />
  );
}